import {assert} from 'Asserts';
import {
  BenchlingExpressionFunction,
  BenchlingExpressionFunctionParameter,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {validateCompleteBenchlingExpression} from 'benchling-expressions/BenchlingExpressionUtils';
import {BenchlingExpressionFunctionName} from 'benchling-expressions/BenchlingFunctionSignatures';
import BenchlingConfig from 'BenchlingConfig';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {
  CreateOrUpdateRouterFunctionSpec,
  ROUTER_ERROR_TYPES,
} from 'processes/modals/router-config/RouterConfigConstants';
import {getPartialExpression} from 'processes/modals/router-config/RouterConfigUtils';

import {getAllowedOperatorAndValueInfo} from './getAllowedOperatorAndValueInfo';

export const DEFAULT_ERROR_MESSAGE = "Make sure all routes are fully configured and don't have errors.";
export const DUPLICATED_NODE_ERROR_TEXT = 'Routes cannot have the same destination node.';
export const DUPLICATED_NODE_ERROR_SUPPORTING_INFO =
  'Select a different destination node for at least one of these routes.';
export const MAXIMUM_EXPRESSIONS_EXCEEDED_ERROR_TEXT =
  "You've reached the maximum number of conditions in a router.";
export const MAXIMUM_EXPRESSIONS_EXCEEDED_SUPPORTING_INFO =
  'Delete at least one condition to save your edits.';

interface RouterConfigValidationResult {
  errorMessage: string | null;
  supportingInfo?: string;
  isBannerError: boolean;
  duplicatedNodes?: Set<string>;
  errorType: ROUTER_ERROR_TYPES;
}

// Check that all fields are present on default function
function validateDefaultFunctionSpec(
  defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec | null
): string | null {
  if (defaultFunctionSpec == null || defaultFunctionSpec.edgeConfigReference === undefined) {
    return DEFAULT_ERROR_MESSAGE;
  }
  return null;
}

function validateFunctionSpec(functionSpec: CreateOrUpdateRouterFunctionSpec): boolean {
  return !(functionSpec.edgeConfigReference === undefined || functionSpec.expression == null);
}

function validateOperatorIsAllowed(
  expression: BenchlingExpressionFunction,
  allSchemasById: SchemaById
): boolean {
  const schemaLookup = expression.parameters?.[0].expression;
  assert(
    schemaLookup?.type === BenchlingExpressionType.SCHEMA_LOOKUP,
    'Expected first parameter to be a schema lookup'
  );
  const lookupConfig = schemaLookup.lookupConfig;
  const {allowedOperators} = lookupConfig
    ? getAllowedOperatorAndValueInfo(lookupConfig, allSchemasById[schemaLookup.schema]!)
    : {allowedOperators: null};
  return allowedOperators?.find(({functionName}) => functionName === expression?.name) != null;
}

function validateExpressionFunctionFormat(
  expression: BenchlingExpressionFunction,
  allSchemasById: SchemaById,
  expressionCounter: {counter: number}
): boolean {
  expressionCounter.counter += 1;
  if (
    (expression.name != null && expression.name === BenchlingExpressionFunctionName.AND) ||
    expression.name === BenchlingExpressionFunctionName.OR
  ) {
    if (expression.parameters.length > BenchlingConfig.WORKFLOWS_MAX_CONDITIONS_PER_ROUTER) {
      return false;
    }
    return expression.parameters.every((param: BenchlingExpressionFunctionParameter) => {
      if (param.expression.type !== BenchlingExpressionType.FUNCTION) {
        return false;
      }
      return validateExpressionFunctionFormat(param.expression, allSchemasById, expressionCounter);
    });
  } else if (!validateCondition(expression)) {
    return false;
  } else if (!validateOperatorIsAllowed(expression, allSchemasById)) {
    return false;
  }
  return true;
}

function validateCondition(expression: BenchlingExpressionFunction): boolean {
  return (
    expression.parameters.length === 2 ||
    expression.parameters[0].expression.type === BenchlingExpressionType.SCHEMA_LOOKUP ||
    expression.parameters[1].expression.type === BenchlingExpressionType.CONSTANT_VALUE
  );
}

function validateCreateOrUpdateRouterFunctionSpec(
  functionSpec: CreateOrUpdateRouterFunctionSpec,
  allSchemasById: SchemaById,
  expressionCounter: {counter: number}
): boolean {
  const expression = getPartialExpression(functionSpec.expression);

  if (!validateFunctionSpec(functionSpec)) {
    return false;
  } else if (expression == null) {
    return false;
  } else if (!validateCompleteBenchlingExpression(expression)) {
    return false;
  } else if (BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS) {
    if (!validateExpressionFunctionFormat(expression, allSchemasById, expressionCounter)) {
      return false;
    }
  } else if (!validateCondition(expression)) {
    return false;
  } else if (!validateOperatorIsAllowed(expression, allSchemasById)) {
    return false;
  }
  return true;
}

function validateFunctionSpecs(
  functionSpecs: Array<CreateOrUpdateRouterFunctionSpec>,
  allSchemasById: SchemaById
): RouterConfigValidationResult | null {
  const expressionsLimit = BenchlingConfig.WORKFLOWS_MAX_EXPRESSIONS_PER_ROUTER;
  const expressionCounter = {
    counter: 0,
  };
  for (const functionSpec of functionSpecs) {
    if (!validateCreateOrUpdateRouterFunctionSpec(functionSpec, allSchemasById, expressionCounter)) {
      return {
        errorMessage: DEFAULT_ERROR_MESSAGE,
        isBannerError: false,
        errorType: ROUTER_ERROR_TYPES.GENERIC_ERROR,
      };
    }
  }
  if (expressionCounter.counter > expressionsLimit) {
    return {
      errorMessage: MAXIMUM_EXPRESSIONS_EXCEEDED_ERROR_TEXT,
      supportingInfo: MAXIMUM_EXPRESSIONS_EXCEEDED_SUPPORTING_INFO,
      isBannerError: true,
      errorType: ROUTER_ERROR_TYPES.TOO_MANY_EXPRESSIONS,
    };
  }
  return null;
}

// Exported for test only
export function validateAllFunctionSpecs(
  defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec,
  functionSpecs: Array<CreateOrUpdateRouterFunctionSpec>,
  allSchemasById: SchemaById
): RouterConfigValidationResult | null {
  const defaultFunctionSpecError = validateDefaultFunctionSpec(defaultFunctionSpec);
  if (defaultFunctionSpecError != null) {
    return {
      errorMessage: DEFAULT_ERROR_MESSAGE,
      isBannerError: false,
      errorType: ROUTER_ERROR_TYPES.GENERIC_ERROR,
    };
  }

  const duplicatedNodes = getDuplicateNextNodes(defaultFunctionSpec, functionSpecs);
  if (duplicatedNodes.size > 0) {
    return {
      errorMessage: DUPLICATED_NODE_ERROR_TEXT,
      supportingInfo: DUPLICATED_NODE_ERROR_SUPPORTING_INFO,
      isBannerError: true,
      errorType: ROUTER_ERROR_TYPES.DUPLICATED_NODE_ERROR_TYPE,
      duplicatedNodes,
    };
  }

  return validateFunctionSpecs(functionSpecs, allSchemasById);
}

/**
 * Gets all functions who share a destination node with some other function.
 * Exported for test only.
 * @return Set of Client IDs of the functions with duplicated next nodes.
 */
export function getDuplicateNextNodes(
  defaultFunctionSpec: CreateOrUpdateRouterFunctionSpec,
  functionSpecs: Array<CreateOrUpdateRouterFunctionSpec>
): Set<string> {
  const outputId = '_output';
  const nodeToFunctionIdMap = new Map<string, string>();
  const invalidFunctionIds = new Set<string>();
  const functionIdsToFlowchartOutput = new Set<string>();
  const defaultNextNodeId =
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    defaultFunctionSpec.edgeConfigReference === null
      ? outputId
      : defaultFunctionSpec.edgeConfigReference?.toNodeClientId;
  if (defaultNextNodeId) {
    nodeToFunctionIdMap.set(defaultNextNodeId, defaultFunctionSpec.clientId);
  }
  for (const functionSpec of functionSpecs) {
    const nextNodeId =
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      functionSpec.edgeConfigReference === null ? outputId : functionSpec.edgeConfigReference?.toNodeClientId;
    if (nextNodeId) {
      if (nodeToFunctionIdMap.has(nextNodeId)) {
        invalidFunctionIds.add(functionSpec.clientId);
        invalidFunctionIds.add(nodeToFunctionIdMap.get(nextNodeId)!);
      } else {
        nodeToFunctionIdMap.set(nextNodeId, functionSpec.clientId);
      }
    }
  }
  if (functionIdsToFlowchartOutput.size > 1) {
    return new Set<string>([...functionIdsToFlowchartOutput, ...invalidFunctionIds]);
  }
  return invalidFunctionIds;
}
